import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";

import * as K from "../../lib/const.js";
import { t } from "../contract/strings";
import {
  finishEnrollment,
  rerecordVoice,
  recordTake,
  TAKE_COUNT_IDLE,
  TAKE_SCRIPTS,
  type Take,
} from "../engine/enrollment";
import { ensureModelReady, isModelReady, ModelLoadError, warmModel } from "../engine/model-readiness";
import { useApp } from "../state/store";
import {
  ArrowRightIcon,
  Button,
  CheckIcon,
  DetailsPanel,
  EqBarsIcon,
  LevelBars,
  LockIcon,
  MicIcon,
  ModelProgress,
  Overlay,
  RecordVoiceOverIcon,
  Row,
  ScreenHeading,
  StatusLine,
  SubHeading,
} from "../ui";
import { Screen } from "./screen";
import { sayOn, useScreenStatus } from "./use-screen-status";
import { captureAnalytics } from "../analytics";

/**
 * Setting up this device: a name, then calibration takes, then the receipt.
 *
 * When the account already carries a name a sign-in provider gave (#1233), the name
 * step opens with it filled in, so Continue is all it takes -- App Review refused
 * asking for a name Apple had already handed over (Guideline 4). It stays editable;
 * a person with no name on the account -- hidden from Apple, or signed in by email --
 * gets the empty field as before.
 *
 * The steps share one grid cell rather than running one after the other, so the box is
 * always as tall as the taller step and the takes stage lands exactly where the name
 * field was. The actions are a common tail beneath every phase, so the button under a
 * thumb does not move when the step changes; the whole screen holds still, only its
 * middle swaps.
 *
 * The privacy note sits outside the swap on purpose: it is true in every step of voice
 * templating -- including the receipt -- and a promise that blinks out mid-flow reads
 * as a caveat.
 *
 * The inactive step is `invisible`, NOT unmounted and not `hidden`. Removing it would
 * pull it out of layout, and the height it reserves is the entire point of the
 * arrangement. `visibility` still takes it out of the accessibility tree and out of tab
 * order, so nothing behind the curtain is reachable or announced.
 */
export function Enroll({ rerecord = false }: { rerecord?: boolean } = {}) {
  const navigate = useNavigate();
  const [status, say] = useScreenStatus();
  const enrollmentStarted = useRef(false);

  // A name a sign-in provider already gave (#1233) prefills the field, cleaned to what
  // the field itself would keep: trimmed the way `finish` trims, never longer than its
  // `maxLength`. A re-record keeps the name it had.
  const accountName = useApp((s) => s.account?.name);
  const knownName = rerecord ? "" : (accountName ?? "").trim().slice(0, 40).trim();

  const [name, setName] = useState(knownName);
  const [briefing, setBriefing] = useState(false);
  const [started, setStarted] = useState(rerecord);
  // The account can land after this screen first rendered: a name that turns up while
  // the field is still untouched is filled in the same way. A typed name always wins.
  const untouched = !started && !briefing && name === "";
  useEffect(() => {
    if (knownName && untouched) setName(knownName);
    // Only the name's arrival triggers this; a flow already under way is never reset.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [knownName]);
  const [done, setDone] = useState(false);
  const [takes, setTakes] = useState<Take[]>([]);
  const [busy, setBusy] = useState(false);
  const [countIn, setCountIn] = useState<number | null>(null);
  // Finishing borrows none of `busy`: that flag drives the recording card, and setting
  // it here would flip the settled screen back to a recorder mid-upload -- a flicker
  // between two states that are both already true.
  const [finishing, setFinishing] = useState(false);
  const [live, setLive] = useState(false);
  const [countdown, setCountdown] = useState<string>(TAKE_COUNT_IDLE);
  // The meter reads this every frame; it must not be state, or the card re-renders 60
  // times a second to paint a bar the component already owns.
  const peak = useRef<(() => number) | null>(null);

  const named = rerecord || name.trim().length > 0;
  const enough = takes.length >= K.ENROLL_TAKES;
  // The phrase on the card is always the next one to speak; the three take cards show progress.
  const script = TAKE_SCRIPTS[takes.length % TAKE_SCRIPTS.length];
  const flow = rerecord ? "rerecord" : "new";

  useEffect(() => {
    if (enrollmentStarted.current) return;
    enrollmentStarted.current = true;
    captureAnalytics("enrollment_started", { flow });
  }, [flow]);

  /**
   * Voice setup is the first screen a newly signed-in device sees, before Home, so it warms
   * the voice model itself (#658, #659); each take then waits for it, with progress, before
   * the microphone opens -- nobody should read a line aloud and then wait for a download.
   */
  const modelWait = useApp((s) => s.modelWait) === "take";
  /** Whether this screen set `modelWait`, so leaving clears its own and nobody else's. */
  const ownsWait = useRef(false);
  /** The wait came up: the dialog is viewed once per entry (#1085). */
  function showModelWait() {
    if (!ownsWait.current) captureAnalytics("dialog_viewed", { dialog: "model_wait" });
    ownsWait.current = true;
    useApp.setState({ modelWait: "take" });
  }
  /** The wait is over and the take goes ahead: it completed, so it is not a dismissal. */
  function clearModelWait() {
    ownsWait.current = false;
    useApp.setState({ modelWait: null });
  }
  /** Cancel, or leaving while the wait was up without a take: an explicit exit (#1085). */
  function dismissModelWait() {
    if (!ownsWait.current) return;
    ownsWait.current = false;
    captureAnalytics("dialog_dismissed", { dialog: "model_wait" });
    useApp.setState({ modelWait: null });
  }
  const modelAbort = useRef<AbortController | null>(null);
  useEffect(() => {
    void warmModel();
    return () => {
      modelAbort.current?.abort();
      dismissModelWait();
    };
    // Leaving the screen ends its own wait, once (#1085).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function cancelModelWait() {
    modelAbort.current?.abort();
    modelAbort.current = null;
    dismissModelWait();
  }

  async function take() {
    setBusy(true);
    const abort = new AbortController();
    modelAbort.current = abort;
    if (!isModelReady()) showModelWait();
    try {
      await ensureModelReady({ signal: abort.signal });
    } catch (err) {
      // A failed load stays on screen with Try again; a Cancel just puts the button back.
      if (!(err instanceof ModelLoadError)) clearModelWait();
      setBusy(false);
      return;
    } finally {
      if (modelAbort.current === abort) modelAbort.current = null;
    }
    clearModelWait();
    setCountIn(0);
    const result = await recordTake(takes, {
      onCountIn: (left) => setCountIn(left),
      onCountdown: (left) => setCountdown(left > 0 ? String(left) : TAKE_COUNT_IDLE),
      onStatus: (message, kind) => say(message, kind),
      onLive: (read) => {
        peak.current = read;
        setLive(read !== null);
      },
    });
    if (result.take) {
      const step = takes.length + 1;
      if (step <= 3) captureAnalytics("enrollment_step_completed", { flow, step: step as 1 | 2 | 3 });
      setTakes((all) => [...all, result.take as Take]);
    } else if (result.status.kind === "err") {
      captureAnalytics("enrollment_failed", { flow, category: "capture" });
    }
    say(result.status.message, result.status.kind);
    setCountIn(null);
    setCountdown(TAKE_COUNT_IDLE);
    setBusy(false);
  }

  async function finish() {
    if (!rerecord && !named) {
      say(t("enroll.error_name_needed"), "err");
      return;
    }
    setFinishing(true);
    try {
      if (rerecord) {
        await rerecordVoice(takes);
        captureAnalytics("enrollment_completed", { flow });
        sayOn("/account", t("enroll.voice_profile_updated"), "ok");
        navigate("/account");
        return;
      }
      const warning = await finishEnrollment(name.trim(), takes);
      captureAnalytics("enrollment_completed", { flow });
      if (warning) {
        // A soft failure (the template saved, something else complained) still goes home,
        // where the toast can be read at leisure rather than inside the flow.
        sayOn("/", warning, "warn");
        navigate("/");
        return;
      }
      setDone(true);
    } catch (err) {
      captureAnalytics("enrollment_failed", { flow, category: "save" });
      say(t("enroll.error_save", { flow: rerecord ? t("enroll.flow_rerecord") : t("enroll.flow_setup"), message: (err as Error).message }), "err");
    } finally {
      setFinishing(false);
    }
  }

  // The promise, stated once so both places that need it say exactly the same thing:
  // the calibration phases keep it below the fold of their actions, and the briefing
  // carries it before anybody is asked to speak.
  const privacyNote = (
    <div className="flex items-start gap-2 my-gutter p-gutter rounded-control bg-surface-2 border border-line">
      <LockIcon className="size-icon-xs flex-none mt-nudge text-ink-soft" />
      <p className="m-0 text-note text-ink-soft">
        {t("enroll.privacy_note")}
      </p>
    </div>
  );

  return (
    <Screen name="enroll">
      {!done && <ScreenHeading className="normal-case">{rerecord ? t("enroll.rerecord_heading") : t("enroll.new_heading")}</ScreenHeading>}

      {done ? (
        /* The receipt: what was made, stated as facts the screen actually knows --
           nothing here is invented, because a fabricated accuracy score would be a
           claim about the microphone's future, and the system holds no such number. */
        <div className="flex flex-col items-center gap-6 my-6 w-full">
          <div className="size-tile-lg rounded-panel border border-line-strong bg-surface-2 flex items-center justify-center">
            <CheckIcon className="size-tile-md text-accent" />
          </div>
          <div className="flex flex-col items-center text-center gap-tight">
            <h2 className="m-0 font-title text-stat font-bold tracking-headline uppercase text-accent">
              {t("enroll.voice_setup_complete")}
            </h2>
          </div>
        </div>
      ) : (
        /* `*:m-0` on the step, not `m-0` on each child, and that is the only spelling that
           works: utilities have equal specificity, Tailwind emits `m-0` before `my-4`, and
           a child override therefore loses. A container telling its children the grid owns
           the rhythm here, so nothing double-gaps. */
        <div className="grid mt-4 mb-6">
          <div
            className={`[grid-area:1/1] grid grid-rows-[auto_1fr] gap-6 *:m-0 ${started ? "hidden" : ""}`}
          >
            {/* The identifier, set like a headline because it is the one thing this step
                exists to collect. Its label carries the accent dot and brightens with focus;
                the three bars at the right end dance while the field holds focus -- the
                microphone's own promise that a voice is coming. Off for anyone who asked
                not to be moved. */}
            <div className="group flex flex-col gap-2">
              <label className="block">
                <span className="mb-tight font-code text-label font-semibold uppercase tracking-label text-accent opacity-60 transition-opacity group-focus-within:opacity-100">
                  {t("enroll.your_name")}
                </span>
                <span className="relative block">
                  <input
                    type="text"
                    autoComplete="name"
                    placeholder={t("enroll.your_name_placeholder")}
                    maxLength={40}
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full py-3 px-gutter rounded-control border border-line-strong bg-surface-2 font-title text-name font-bold tracking-name text-ink placeholder:normal-case placeholder:tracking-normal placeholder:text-ink-soft placeholder:opacity-60 focus-visible:border-accent"
                  />
                  <span
                    className="absolute right-gutter top-1/2 -translate-y-1/2 flex items-end gap-1 pointer-events-none opacity-40 transition-opacity group-focus-within:opacity-100"
                    aria-hidden="true"
                  >
                    <span className="w-1 h-4 rounded-pill bg-accent animate-eq-1 motion-reduce:animate-none" />
                    <span className="w-1 h-6 rounded-pill bg-accent animate-eq-2 motion-reduce:animate-none" />
                    <span className="w-1 h-3 rounded-pill bg-accent animate-eq-3 motion-reduce:animate-none" />
                  </span>
                </span>
              </label>
              <p className="m-0 max-w-[90%] text-note leading-row text-ink-soft">
                {t("enroll.name_hint")}
              </p>
            </div>

            {/* The preview lies a little -- one rung of the very ladder a session draws --
                so what the eye approves here is exactly what the summary shows later.
                `self-center` stands it midway through the row's leftover height instead
                of stacking it right under the field. */}
            <div className="self-center">
              <div className="flex flex-col gap-tight">
                <SubHeading className="m-0">{t("enroll.preview")}</SubHeading>
                {/* R01 variant B: name only -- no second speaker and no clock node. */}
                <div className="relative">
                    <span className="absolute left-0 top-0 bottom-0 border-l border-accent opacity-30" aria-hidden="true" />
                    <div className="relative pl-gutter py-row">
                      <div className="flex items-baseline gap-2">
                        <span className="font-code text-label font-bold uppercase tracking-label text-accent">
                          {name.trim() || t("enroll.speaker_placeholder")}
                        </span>
                      </div>
                    </div>
                </div>
              </div>
            </div>
          </div>

          <div
            className={`[grid-area:1/1] flex flex-col gap-4 *:m-0 ${started ? "" : "hidden"}`}
          >
            <div className="grid grid-cols-3 gap-2" role="list" aria-label={t("enroll.setup_steps")}>
              {Array.from({ length: K.ENROLL_TAKES }, (_, index) => {
                const complete = index < takes.length;
                const current = index === takes.length;
                return (
                  <div
                    key={index}
                    role="listitem"
                    aria-current={current ? "step" : undefined}
                    className={`flex min-h-12 items-center justify-center gap-1.5 rounded-control border px-1.5 ${
                      current
                        ? "border-accent bg-surface-2"
                        : complete
                          ? "border-line-strong bg-surface"
                          : "border-line bg-surface"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 flex-none items-center justify-center rounded-full border font-code text-count font-semibold tabular-nums ${
                        current
                          ? "border-accent bg-accent text-accent-ink"
                          : complete
                            ? "border-ok bg-ok text-accent-ink"
                            : "border-line-strong text-ink-soft"
                      }`}
                    >
                      {complete ? <CheckIcon className="h-3.5 w-3.5" /> : index + 1}
                    </span>
                    <span className="font-code text-label font-semibold uppercase tracking-label text-ink">
                      {t("enroll.take_n", { n: index + 1 })}
                    </span>
                  </div>
                );
              })}
            </div>

            {/*
                Both card states share one skeleton -- header row, phrase, and a fixed
                h-32 stage zone -- because the swap happens under somebody's thumb: a
                card that shrinks when recording starts reads as the page flinching.
                The phrase is set at the display size in BOTH states; it is read aloud
                from arm's length, which is a typographic job before it is a stylistic
                one, and identical type is what makes the swap invisible.
            */}
            {busy && countIn === null ? (
              /* While the take runs: the dot says it before the word does, and the bars
                 are the real reading -- a field that moves with your voice is the only
                 proof the microphone is live. Before speech reaches them, the countdown
                 stands in. */
              <div className="rounded-panel border border-line-strong bg-surface-2 p-pad overflow-hidden">
                <div className="flex items-center justify-between mb-stack">
                  <span className="flex items-center gap-nudge font-code text-label font-semibold uppercase tracking-label text-danger">
                    <span className="size-dot rounded-full bg-danger animate-pulse motion-reduce:animate-none flex-none" aria-hidden="true" />
                    {t("enroll.listening")}
                  </span>
                </div>
                <p className="m-0 font-title text-name font-bold tracking-name text-accent text-center leading-tight">
                  &ldquo;{script}&rdquo;
                </p>
                {/* The count ticks through the take itself -- seven digits, then silence --
                    above the bars. It is aria-hidden on purpose: a spoken second-by-second
                    count in a live region is noise, and the status line already announced
                    the recording. */}
                <div className="h-32 flex flex-col items-center justify-center gap-tight">
                  <span className="font-title text-live-count font-bold text-accent tabular-nums" aria-hidden="true">
                    {countdown}
                  </span>
                  {live ? (
                    <LevelBars read={() => peak.current?.() ?? 0} active className="h-16" />
                  ) : null}
                </div>
              </div>
            ) : enough && countIn === null ? (
              /* Every take is in: the target phrase retires and the screen states the
                 fact. No plate around it -- a fact this settled does not need a card to
                 hold it -- and flex-1 with justify-center stands it midway through what
                 the rail leaves, instead of hanging it under the top edge. The tile
                 carries flex-none so nothing can press it out of square. */
              <div className="relative flex-1 flex flex-col items-center text-center justify-center gap-gutter py-stack">
                  <div className="size-tile-sm flex-none rounded-panel border border-line-strong bg-surface-3 flex items-center justify-center">
                    <CheckIcon className="size-icon-xl text-accent" />
                  </div>
                  <div className="flex flex-col items-center gap-tight">
                    <p className="m-0 font-title text-stat font-bold tracking-headline uppercase text-accent">
                      {t("enroll.takes_ready")}
                    </p>

                  </div>
              </div>
            ) : (
              /* Between takes the same lectern: the phrase at the same size, the stage
                 holding the one sentence of coaching instead of the count. */
              <div className="relative rounded-panel border border-line-strong bg-surface-2 p-pad overflow-hidden">
                <div className="flex items-center justify-between mb-stack">
                  <span className="flex items-center gap-1 font-code text-label font-semibold uppercase tracking-label text-ink-soft">
                    <EqBarsIcon className="size-icon-xs text-accent" />
                    {t("enroll.read_aloud")}
                  </span>
                </div>
                <p className="m-0 font-title text-name font-bold tracking-name text-accent text-center leading-tight">
                  &ldquo;{script}&rdquo;
                </p>
                <div className="h-32 flex items-center justify-center">
                  <p className="m-0 max-w-[90%] text-note text-ink-soft text-center opacity-80">
                    {t("enroll.speak_naturally")}
                  </p>
                </div>
                {countIn !== null && countIn > 0 ? (
                  <div
                    className="absolute inset-0 z-10 grid place-items-center bg-surface-2"
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    <span className="relative grid h-24 w-24 place-items-center rounded-full border border-accent/40 bg-surface-2">
                      <span
                        className="absolute inset-0 rounded-full border border-accent animate-voice-ring motion-reduce:animate-none"
                        aria-hidden="true"
                      />
                      <span
                        key={countIn}
                        className="font-title text-live-count font-bold text-accent tabular-nums animate-voice-count motion-reduce:animate-none"
                      >
                        {countIn}
                      </span>
                    </span>
                  </div>
                ) : null}
              </div>
            )}

          </div>
        </div>
      )}

      {modelWait ? (
        <ModelProgress
          className="mt-4"
          onRetry={() => {
            captureAnalytics("retry_tapped", { surface: "model_load" });
            void take();
          }}
          onCancel={cancelModelWait}
        />
      ) : done ? (
        <Button onClick={() => navigate("/")}>
          <span className="inline-flex items-center justify-center gap-2">
            {t("enroll.finish_go_home")}
            <ArrowRightIcon className="size-icon-xs" />
          </span>
        </Button>
      ) : !started ? (
        <Button className="mt-4" disabled={!named} onClick={() => setBriefing(true)}>
          <span className="inline-flex items-center justify-center gap-2">
            {t("enroll.continue")}
            <ArrowRightIcon className="size-icon-xs" />
          </span>
        </Button>
      ) : enough ? (
        /* Emphasis swaps at the third take. Before it, recording is the thing to do;
           after it, finishing is -- and the accent button offering an *optional* fourth
           take beside a quiet "Finish setup" is the wrong way round for the only screen
           standing between somebody and the app. */
        <Row>
          <Button variant="ghost" disabled={busy || finishing} onClick={take}>
            {t("enroll.another_take")}
          </Button>
          <Button disabled={busy || finishing} onClick={finish}>
            {rerecord ? t("enroll.save_voice_profile") : t("enroll.finish_setup")}
          </Button>
        </Row>
      ) : (
        <Button disabled={busy} onClick={take}>
          <span className="inline-flex items-center justify-center gap-2">
            <MicIcon className="size-icon-xs" />
            {busy ? (countIn === null ? t("enroll.listening_ellipsis") : t("enroll.starting")) : t("enroll.start_take", { n: takes.length + 1 })}
          </span>
        </Button>
      )}

      {/* Below the actions, but never hidden: a failure puts its explanation here, and
          hiding it would swallow the one message saying why. */}
      <StatusLine kind={status.kind}>{status.message}</StatusLine>
      {status.message === t("session.signed_out") ? (
        <DetailsPanel summary={t("common.why")}><p>{t("session.identity_detail")}</p></DetailsPanel>
      ) : null}

      {started && !done ? (
        <DetailsPanel summary={t("enroll.about_voice_setup")}>
          <p>{t("enroll.about_p1")}</p>
          <p>{t("enroll.about_p2")}</p>
          {rerecord ? <p>{t("enroll.about_p3")}</p> : null}
        </DetailsPanel>
      ) : null}

      {/* The briefing stands between the name and the first read: what is about to
          happen, and what it asks of the room. The idle equalizer is the same bars the
          takes will wake, held at their floors -- a promise, not a decoration. */}
      {briefing ? (
        <Overlay onClose={() => setBriefing(false)}>
          <div className="flex flex-col items-center text-center gap-gutter">
            <RecordVoiceOverIcon className="size-tile-lg text-accent" />
            <p className="m-0 mt-stack font-title text-name font-bold tracking-hero text-ink">
              {t("enroll.find_quiet")}
            </p>
            <p className="m-0 max-w-[90%] text-script leading-row text-ink-soft">
              {t("enroll.briefing")}
            </p>
            <p>{t("enroll.speak_naturally")}</p>
            {privacyNote}
            <Button
              className="mt-stack self-stretch"
              onClick={() => {
                setBriefing(false);
                setStarted(true);
              }}
            >
              <span className="inline-flex items-center justify-center gap-2">
                {t("enroll.set_up_my_voice")}
                <ArrowRightIcon className="size-icon-xs" />
              </span>
            </Button>
          </div>
        </Overlay>
      ) : null}
    </Screen>
  );
}
